import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import SchemaDetailContext from '@atom/components/schemaDetail/SchemaDetailContext';
import { ATTRIBUTE_GROUP_CREATE } from '@atom/graph/schema';
import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ControlPointDuplicateIcon } from '@atom/mui/icons';

import AddItemModal from '../AddItemModal';

import classes from './subItemDetail.module.css';

interface Props {
  setCreatedAttributeGroupId: Dispatch<SetStateAction<string>>;
}

const DetailHeader = ({ setCreatedAttributeGroupId }: Props) => {
  const container = useRef<HTMLDivElement>(null);

  const { schemaTree, refetchSchemaTree, selectedSubItem } =
    useContext(SchemaDetailContext);

  const [overflowing, setOverflowing] = useState<boolean>(false);
  const [openAttributeGroupCreate, setOpenAttributeGroupCreate] =
    useState<boolean>(false);

  const [createAttributeGroup, { loading: addingGroup }] = useMutation(
    ATTRIBUTE_GROUP_CREATE,
  );

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [selectedSubItem.name]);

  const handleAttributeGroupCreate = async (name: string) => {
    const { data } = await createAttributeGroup({
      variables: {
        input: {
          schemaId: selectedSubItem.id,
          name,
        },
      },
    });

    await refetchSchemaTree();

    const newAttributeGroupId = R.last(
      data?.attributeGroupCreate?.attributeGroups,
    )?.id;

    setCreatedAttributeGroupId(newAttributeGroupId);
    setOpenAttributeGroupCreate(false);
  };

  const showAddButton = !schemaTree?.isPublished;

  return (
    <>
      <div className={classes.detailHeaderContainer}>
        <div className={classes.detailHeaderText} ref={container}>
          {!overflowing ? (
            selectedSubItem.name
          ) : (
            <Tooltip title={selectedSubItem.name} placement="bottom-start">
              <p className={classes.detailHeaderText}>{selectedSubItem.name}</p>
            </Tooltip>
          )}
        </div>
        {showAddButton && (
          <IconButton
            onClick={() => setOpenAttributeGroupCreate(true)}
            size="small"
            tooltip="Add Group"
          >
            <Icon icon={ControlPointDuplicateIcon} />
          </IconButton>
        )}
      </div>
      <AddItemModal
        open={openAttributeGroupCreate}
        closeModal={() => setOpenAttributeGroupCreate(false)}
        type="Group"
        handleSave={handleAttributeGroupCreate}
        isDisabled={addingGroup}
      />
    </>
  );
};

export default DetailHeader;
